/** QuickPlan — Export STL binaire pour l'impression 3D.
 *
 * Le maillage est généré ici et non repris de la vue 3D : un slicer exige des surfaces
 * fermées, et l'affichage n'a ni la finesse voulue (cylindres à 24 facettes) ni la garantie
 * de sommets strictement confondus. Toute primitive QuickPlan est un prisme (pavé, section
 * extrudée, cylindre à facettes) : chaque sommet n'est calculé qu'une fois, donc chaque
 * coquille est fermée au bit près, normales vers l'extérieur.
 *
 * Conventions de sortie :
 *  - unités en MILLIMÈTRES (ce que supposent les slicers ; STL ne porte pas d'unité) ;
 *  - Z vers le haut (repère de QuickPlan comme des slicers) ;
 *  - pièces conservées dans leur position relative, l'ensemble posé sur le plateau (min Z = 0)
 *    et centré en X/Y.
 *
 * Une coquille par primitive : pas d'union booléenne (QuickPlan n'a pas de noyau CAO), les
 * pièces qui se chevauchent restent superposées. Les perçages de la notice ne sont pas
 * creusés : ils ne font pas partie de la géométrie des pièces.
 */
import * as THREE from 'three';
import { rotMatrix, apply, add, extrudeMap } from '../../shared/resolve.js';
import { resolveSection, polygonArea } from '../../shared/sections.js';
import { cleanLoop } from '../../shared/step.js';
import { frames, hiddenSet, resolvedOf } from '../geom.js';

/** Écart maximal (mm) entre un cylindre et ses facettes : sous la résolution d'une imprimante. */
const CHORD_TOLERANCE = 0.02;

/** Nombre de facettes d'un cylindre : multiple de 4 pour que l'encombrement reste exact. */
export function cylinderSegments(r) {
  const n = Math.PI / Math.acos(1 - Math.min(CHORD_TOLERANCE / r, 1));
  return Math.min(360, Math.max(16, Math.ceil(n / 4) * 4));
}

/** Retire les sommets alignés : la triangulation les ignore, ce qui ouvrirait la coquille. */
function dropCollinear(loop) {
  return loop.filter((q, i) => {
    const a = loop[(i + loop.length - 1) % loop.length];
    const b = loop[(i + 1) % loop.length];
    const u = [q[0] - a[0], q[1] - a[1]];
    const v = [b[0] - q[0], b[1] - q[1]];
    const sin = (u[0] * v[1] - u[1] * v[0]) / (Math.hypot(...u) * Math.hypot(...v));
    return Math.abs(sin) > 1e-9 || u[0] * v[0] + u[1] * v[1] < 0;
  });
}

/** Maillage fermé d'un prisme : section extrudée sur t ∈ [0, L], dans le repère de la primitive. */
function prismMesh(outerLoop, holeLoops, L, axis) {
  let outer = dropCollinear(cleanLoop(outerLoop));
  if (!(L > 0) || outer.length < 3 || Math.abs(polygonArea(outer)) < 1e-9) return null;
  const holes = holeLoops.map((h) => dropCollinear(cleanLoop(h))).filter((h) => h.length >= 3)
    .map((h) => (polygonArea(h) > 0 ? h.slice().reverse() : h));
  if (polygonArea(outer) < 0) outer = outer.slice().reverse();

  // Contour dans le sens trigonométrique, alésages dans le sens horaire : côté × t = extérieur.
  const flat = [outer, ...holes].flat();
  const n = flat.length;
  const vertices = [
    ...flat.map((q) => extrudeMap(axis, q[0], q[1], 0)),
    ...flat.map((q) => extrudeMap(axis, q[0], q[1], L)),
  ];
  const faces = [];
  let start = 0;
  for (const loop of [outer, ...holes]) {
    loop.forEach((_, k) => {
      const i = start + k;
      const j = start + (k + 1) % loop.length;
      faces.push([i, j, j + n], [i, j + n, i + n]);
    });
    start += loop.length;
  }
  const v2 = (q) => new THREE.Vector2(q[0], q[1]);
  for (const [a, b, c] of THREE.ShapeUtils.triangulateShape(outer.map(v2), holes.map((h) => h.map(v2)))) {
    const direct = polygonArea([flat[a], flat[b], flat[c]]) > 0;
    const [p, q] = direct ? [b, c] : [c, b];
    faces.push([a + n, p + n, q + n], [a, q, p]); // dessus (+t), dessous (−t)
  }
  return { vertices, faces };
}

/** Maillage d'une primitive résolue, ou null si elle est dégénérée. */
function partMesh(p) {
  if (p.type === 'box') {
    const [x, y, z] = p.size;
    return prismMesh([[0, 0], [x, 0], [x, y], [0, y]], [], z, 'z');
  }
  if (p.type === 'cyl') {
    if (!(p.r > 0)) return null;
    const n = cylinderSegments(p.r);
    const ring = Array.from({ length: n }, (_, k) => {
      const a = (2 * Math.PI * k) / n;
      return [p.r * Math.cos(a), p.r * Math.sin(a)];
    });
    return prismMesh(ring, [], p.h, p.axis);
  }
  const sec = resolveSection(p.section);
  return prismMesh(sec.outer, sec.holes ?? [], p.length, p.axis);
}

/** Nom de fichier : « <projet>.stl », ou « <projet> - <pièce>.stl » pour une seule pièce. */
export function stlFileName(project, piece = '') {
  const clean = (s) => (s ?? '').replace(/[\\/:*?"<>|\u0000-\u001f]+/g, '_').trim();
  return `${[clean(project?.name) || 'projet', clean(piece)].filter(Boolean).join(' - ')}.stl`;
}

/**
 * STL binaire des pièces `ids` (par défaut, tout l'assemblage), pièces masquées exclues.
 * Fonction pure (pas de DOM). Rend { buffer, fileName, triangles, instances, size } ;
 * size = encombrement [x, y, z] en mm.
 */
export function buildStl(project, lookup, { ids = null } = {}) {
  const F = frames(project, lookup);
  const hidden = hiddenSet(project);
  const wanted = ids?.length ? new Set(ids) : null;
  const instances = project.instances.filter((i) => !hidden.has(i.id) && (!wanted || wanted.has(i.id)) && resolvedOf(i, lookup) && F.get(i.id));

  const shells = [];
  const lo = [Infinity, Infinity, Infinity];
  const hi = [-Infinity, -Infinity, -Infinity];
  let triangles = 0;
  for (const inst of instances) {
    const f = F.get(inst.id);
    for (const p of resolvedOf(inst, lookup).parts) {
      const mesh = partMesh(p);
      if (!mesh) continue;
      const M = rotMatrix(p.rot ?? undefined);
      const vertices = mesh.vertices.map((v) => add(apply(f.R, add(apply(M, v), p.pos)), f.t));
      for (const v of vertices) {
        for (let k = 0; k < 3; k++) { lo[k] = Math.min(lo[k], v[k]); hi[k] = Math.max(hi[k], v[k]); }
      }
      shells.push({ vertices, faces: mesh.faces });
      triangles += mesh.faces.length;
    }
  }
  if (!triangles) throw new Error('Aucune pièce visible à exporter');

  const offset = [-(lo[0] + hi[0]) / 2, -(lo[1] + hi[1]) / 2, -lo[2]];
  const buffer = new ArrayBuffer(84 + 50 * triangles);
  const out = new DataView(buffer);
  // En-tête : ne doit pas commencer par « solid » (réservé au STL texte).
  const header = `QuickPlan STL mm Z-up ${project.name ?? ''}`.slice(0, 80);
  for (let k = 0; k < header.length; k++) out.setUint8(k, header.charCodeAt(k) < 127 ? header.charCodeAt(k) : 63);
  out.setUint32(80, triangles, true);
  let at = 84;
  const put = (v) => { for (const x of v) { out.setFloat32(at, x, true); at += 4; } };
  for (const { vertices, faces } of shells) {
    const placed = vertices.map((v) => [v[0] + offset[0] + 0, v[1] + offset[1] + 0, v[2] + offset[2] + 0]);
    for (const [a, b, c] of faces) {
      const u = [placed[b][0] - placed[a][0], placed[b][1] - placed[a][1], placed[b][2] - placed[a][2]];
      const v = [placed[c][0] - placed[a][0], placed[c][1] - placed[a][1], placed[c][2] - placed[a][2]];
      const nx = u[1] * v[2] - u[2] * v[1];
      const ny = u[2] * v[0] - u[0] * v[2];
      const nz = u[0] * v[1] - u[1] * v[0];
      const len = Math.hypot(nx, ny, nz) || 1;
      put([nx / len, ny / len, nz / len]);
      put(placed[a]);
      put(placed[b]);
      put(placed[c]);
      at += 2; // octets d'attribut : à zéro
    }
  }
  return {
    buffer,
    fileName: stlFileName(project, instances.length === 1 ? instances[0].name || lookup(instances[0].componentId)?.name : ''),
    triangles,
    instances: instances.length,
    size: hi.map((v, k) => v - lo[k]),
  };
}
